import { useState, useEffect } from 'react';
import { Ticket, Plus, Copy, Trash2, ToggleLeft, ToggleRight, Loader2, X, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFormattedDate } from '../i18n/dateUtils';
import { adminApi } from '../utils/api';

interface InvitationCode {
  code: string;
  created_at: string;
  used_by: string | null;
  max_uses: number;
  current_uses: number;
  is_disabled: boolean;
}

interface VipCode {
  code: string;
  created_by: string;
  used_by: string | null;
  used_at: string | null;
  is_active: boolean;
  created_at: string;
}

const InvitationCodes = () => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const { formatDateTime } = useFormattedDate();
  const [activeTab, setActiveTab] = useState<'invite' | 'vip'>('invite');

  // 邀请码状态
  const [codes, setCodes] = useState<InvitationCode[]>([]);
  const [inviteLoading, setInviteLoading] = useState(true);

  // VIP 码状态
  const [vipCodes, setVipCodes] = useState<VipCode[]>([]);
  const [vipLoading, setVipLoading] = useState(true);
  const [showVipGenerateModal, setShowVipGenerateModal] = useState(false);
  const [vipGenerateCount, setVipGenerateCount] = useState(1);
  const [vipGenerating, setVipGenerating] = useState(false);
  const [copiedVipCode, setCopiedVipCode] = useState<string | null>(null);

  // 通用状态
  const [error, setError] = useState('');
  const [inviteRequired, setInviteRequired] = useState(true);
  const [defaultUserQuota, setDefaultUserQuota] = useState(30);
  const [defaultVipQuota, setDefaultVipQuota] = useState(1000);
  const [maxConcurrent, setMaxConcurrent] = useState(2);
  const [savingQuota, setSavingQuota] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateCount, setGenerateCount] = useState(1);
  const [generateMaxUses, setGenerateMaxUses] = useState(1);
  const [generating, setGenerating] = useState('');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  useEffect(() => {
    fetchSettings();
    fetchCodes();
    fetchVipCodes();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await adminApi.settings.get();
      const s = response.data.settings || {};
      setInviteRequired(s.invite_required ?? true);
      setDefaultUserQuota(s.default_user_quota ?? 30);
      setDefaultVipQuota(s.default_vip_quota ?? 1000);
      setMaxConcurrent(s.max_concurrent_inferences ?? 2);
    } catch (err: any) {
      console.error(t('invitationCodes.errors.updateSettingsFailed'), err);
    }
  };

  const fetchCodes = async () => {
    try {
      setInviteLoading(true);
      const response = await adminApi.invitationCodes.list();
      setCodes(response.data.codes || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('invitationCodes.errors.fetchFailed'));
    } finally {
      setInviteLoading(false);
    }
  };

  const fetchVipCodes = async () => {
    try {
      setVipLoading(true);
      const response = await adminApi.vipCodes.list();
      setVipCodes(response.data.codes || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('invitationCodes.errors.fetchVipFailed'));
    } finally {
      setVipLoading(false);
    }
  };

  const handleToggleInviteRequired = async () => {
    try {
      const newValue = !inviteRequired;
      await adminApi.settings.update({ invite_required: newValue });
      setInviteRequired(newValue);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('invitationCodes.errors.updateSettingsFailed'));
    }
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setGenerating('generating');
      await adminApi.invitationCodes.generate(generateCount, generateMaxUses);
      setShowGenerateModal(false);
      setGenerateCount(1);
      setGenerateMaxUses(1);
      fetchCodes();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('invitationCodes.errors.generateFailed'));
    } finally {
      setGenerating('');
    }
  };

  const handleGenerateVip = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setVipGenerating(true);
      await adminApi.vipCodes.generate(vipGenerateCount);
      setShowVipGenerateModal(false);
      setVipGenerateCount(1);
      fetchVipCodes();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('invitationCodes.errors.generateVipFailed'));
    } finally {
      setVipGenerating(false);
    }
  };

  const handleDelete = async (code: string) => {
    if (!confirm(t('invitationCodes.confirmDelete', { code }))) return;
    try {
      await adminApi.invitationCodes.delete(code);
      fetchCodes();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('invitationCodes.errors.deleteFailed'));
    }
  };

  const handleDeleteVip = async (code: string) => {
    if (!confirm(t('invitationCodes.confirmDeleteVip', { code }))) return;
    try {
      await adminApi.vipCodes.delete(code);
      fetchVipCodes();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('invitationCodes.errors.deleteVipFailed'));
    }
  };

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    }
  };

  const handleCopyVip = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedVipCode(code);
      setTimeout(() => setCopiedVipCode(null), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedVipCode(code);
      setTimeout(() => setCopiedVipCode(null), 2000);
    }
  };

  const getInviteStatusBadge = (item: InvitationCode) => {
    if (item.is_disabled) {
      return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">{tCommon('status.disabled')}</span>;
    }
    if (item.current_uses >= item.max_uses) {
      return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">{tCommon('status.usedUp')}</span>;
    }
    if (item.current_uses > 0) {
      return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">{tCommon('status.inUse')}</span>;
    }
    return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">{tCommon('status.unused')}</span>;
  };

  const getVipStatusBadge = (item: VipCode) => {
    if (!item.is_active) {
      return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">{tCommon('status.used')}</span>;
    }
    return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">{tCommon('status.unused')}</span>;
  };

  const isLoading = inviteLoading || vipLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('invitationCodes.title')}</h1>
          <p className="text-gray-600 mt-1">{t('invitationCodes.description')}</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-400 hover:text-red-600">
            <X className="w-4 h-4 inline" />
          </button>
        </div>
      )}

      {/* Tab 切换 */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('invite')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'invite' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="flex items-center gap-2"><Ticket className="w-4 h-4" /> {t('invitationCodes.tabs.invite')}</span>
        </button>
        <button
          onClick={() => setActiveTab('vip')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'vip' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="flex items-center gap-2"><Crown className="w-4 h-4" /> {t('invitationCodes.tabs.vip')}</span>
        </button>
      </div>

      {/* 邀请码开关 */}
      <div className="card mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Ticket className="w-5 h-5 text-primary-600" />
            <div>
              <p className="font-medium text-gray-900">{t('invitationCodes.toggle.title')}</p>
              <p className="text-sm text-gray-500">
                {inviteRequired ? t('invitationCodes.toggle.enabled') : t('invitationCodes.toggle.disabled')}
              </p>
            </div>
          </div>
          <button onClick={handleToggleInviteRequired} className="text-gray-400 hover:text-primary-600 transition-colors">
            {inviteRequired ? <ToggleRight className="w-8 h-8 text-primary-600" /> : <ToggleLeft className="w-8 h-8" />}
          </button>
        </div>
      </div>

      {/* 默认配额设置 */}
      <div className="card mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Ticket className="w-5 h-5 text-primary-600" />
          <div>
            <p className="font-medium text-gray-900">{t('invitationCodes.quotaSettings.title')}</p>
            <p className="text-sm text-gray-500">{t('invitationCodes.quotaSettings.description')}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('invitationCodes.quotaSettings.freeUserQuota')}</label>
            <div className="flex items-center gap-2">
              <input type="number" value={defaultUserQuota} onChange={(e) => setDefaultUserQuota(Math.max(1, parseInt(e.target.value) || 1))} className="input w-32" min={1} max={999999} />
              <span className="text-sm text-gray-500">{t('invitationCodes.quotaSettings.timesPerDay')}</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('invitationCodes.quotaSettings.vipUserQuota')}</label>
            <div className="flex items-center gap-2">
              <input type="number" value={defaultVipQuota} onChange={(e) => setDefaultVipQuota(Math.max(1, parseInt(e.target.value) || 1))} className="input w-32" min={1} max={999999} />
              <span className="text-sm text-gray-500">{t('invitationCodes.quotaSettings.timesPerDay')}</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('invitationCodes.quotaSettings.maxConcurrent')}</label>
            <div className="flex items-center gap-2">
              <input type="number" value={maxConcurrent} onChange={(e) => setMaxConcurrent(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))} className="input w-32" min={1} max={10} />
              <span className="text-sm text-gray-500">{t('invitationCodes.quotaSettings.unit')}</span>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={async () => {
              setSavingQuota(true);
              try {
                await adminApi.settings.update({
                  default_user_quota: defaultUserQuota,
                  default_vip_quota: defaultVipQuota,
                  max_concurrent_inferences: maxConcurrent,
                });
                alert(t('invitationCodes.quotaSettings.saveSuccess'));
              } catch (err: any) {
                alert(err?.response?.data?.detail || t('invitationCodes.quotaSettings.saveFailed'));
              } finally {
                setSavingQuota(false);
              }
            }}
            disabled={savingQuota}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            {savingQuota && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('invitationCodes.quotaSettings.saveButton')}
          </button>
        </div>
      </div>

      {/* 邀请码 Tab */}
      {activeTab === 'invite' && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('invitationCodes.inviteTable.title')}</h2>
            <button onClick={() => setShowGenerateModal(true)} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" /> {t('invitationCodes.inviteTable.generateButton')}
            </button>
          </div>
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('invitationCodes.inviteTable.code')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('invitationCodes.inviteTable.createdAt')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('invitationCodes.inviteTable.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('invitationCodes.inviteTable.usedBy')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('invitationCodes.inviteTable.usageCount')}</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('invitationCodes.inviteTable.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {codes.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        <Ticket className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>{t('invitationCodes.inviteTable.empty')}</p>
                      </td>
                    </tr>
                  ) : (
                    codes.map((item) => (
                      <tr key={item.code} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-mono text-sm font-medium text-gray-900 bg-gray-100 px-2 py-1 rounded">{item.code}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateTime(item.created_at)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{getInviteStatusBadge(item)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.used_by || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.current_uses} / {item.max_uses === -1 ? t('invitationCodes.inviteTable.unlimited') : item.max_uses}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => handleCopy(item.code)} className="text-blue-600 hover:text-blue-900" title={tCommon('actions.copy')}>
                              {copiedCode === item.code ? <span className="text-green-600 text-xs">{tCommon('actions.copied')}</span> : <Copy className="w-4 h-4" />}
                            </button>
                            <button onClick={() => handleDelete(item.code)} className="text-red-600 hover:text-red-900" title={tCommon('actions.delete')}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* VIP 兑换码 Tab */}
      {activeTab === 'vip' && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('invitationCodes.vipTable.title')}</h2>
            <button onClick={() => setShowVipGenerateModal(true)} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" /> {t('invitationCodes.vipTable.generateButton')}
            </button>
          </div>
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('invitationCodes.vipTable.code')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('invitationCodes.vipTable.createdAt')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('invitationCodes.vipTable.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('invitationCodes.vipTable.usedBy')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('invitationCodes.vipTable.usedAt')}</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('invitationCodes.vipTable.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {vipCodes.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        <Crown className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>{t('invitationCodes.vipTable.empty')}</p>
                      </td>
                    </tr>
                  ) : (
                    vipCodes.map((item) => (
                      <tr key={item.code} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-mono text-sm font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded">{item.code}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateTime(item.created_at)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{getVipStatusBadge(item)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.used_by || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.used_at ? formatDateTime(item.used_at) : '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            {item.is_active && (
                              <button onClick={() => handleCopyVip(item.code)} className="text-blue-600 hover:text-blue-900" title={tCommon('actions.copy')}>
                                {copiedVipCode === item.code ? <span className="text-green-600 text-xs">{tCommon('actions.copied')}</span> : <Copy className="w-4 h-4" />}
                              </button>
                            )}
                            <button onClick={() => handleDeleteVip(item.code)} className="text-red-600 hover:text-red-900" title={tCommon('actions.delete')}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* 生成邀请码弹窗 */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">{t('invitationCodes.generateModal.title')}</h2>
              <button onClick={() => setShowGenerateModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleGenerate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('invitationCodes.generateModal.countLabel')}</label>
                <input type="number" value={generateCount} onChange={(e) => setGenerateCount(Math.max(1, parseInt(e.target.value) || 1))} className="input" min="1" max="100" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('invitationCodes.generateModal.maxUsesLabel')}</label>
                <input type="number" value={generateMaxUses} onChange={(e) => setGenerateMaxUses(parseInt(e.target.value) || 1)} className="input" min="1" required />
                <p className="text-xs text-gray-500 mt-1">{t('invitationCodes.generateModal.maxUsesHint')}</p>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowGenerateModal(false)} className="flex-1 btn-secondary">{t('invitationCodes.generateModal.cancel')}</button>
                <button type="submit" disabled={!!generating} className="flex-1 btn-primary flex items-center justify-center gap-2">
                  {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('invitationCodes.generateModal.loading')}</> : <><Plus className="w-4 h-4" /> {t('invitationCodes.generateModal.generate')}</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 生成 VIP 兑换码弹窗 */}
      {showVipGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">{t('invitationCodes.generateVipModal.title')}</h2>
              <button onClick={() => setShowVipGenerateModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleGenerateVip} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('invitationCodes.generateVipModal.countLabel')}</label>
                <input type="number" value={vipGenerateCount} onChange={(e) => setVipGenerateCount(Math.max(1, parseInt(e.target.value) || 1))} className="input" min="1" max="100" required />
                <p className="text-xs text-gray-500 mt-1">{t('invitationCodes.generateVipModal.countHint')}</p>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowVipGenerateModal(false)} className="flex-1 btn-secondary">{t('invitationCodes.generateVipModal.cancel')}</button>
                <button type="submit" disabled={vipGenerating} className="flex-1 btn-primary flex items-center justify-center gap-2">
                  {vipGenerating ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('invitationCodes.generateVipModal.loading')}</> : <><Plus className="w-4 h-4" /> {t('invitationCodes.generateVipModal.generate')}</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationCodes;
